/**
 * Componente de Alertas de Saúde da Fila PGMQ
 * 
 * Exibe alertas sobre condições que requerem atenção:
 * - Muitos itens pendentes
 * - Itens stuck em processamento
 * - Taxa de sucesso baixa
 * - Latência alta
 */

import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useFlowQueueAlertsPgmq } from '@/hooks/useFlowQueueMonitorPgmq';

export function PgmqHealthAlerts() {
  const { data: alerts, isLoading } = useFlowQueueAlertsPgmq();

  if (isLoading) {
    return <Skeleton className="h-16 w-full" />;
  }

  // Se não há alertas, mostra status saudável
  if (!alerts || alerts.length === 0) {
    return (
      <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-800 dark:text-green-200">
          Sistema operando normalmente
        </AlertTitle>
        <AlertDescription className="text-green-700 dark:text-green-300">
          Nenhum problema detectado na fila PGMQ. Todas as métricas estão dentro dos limites esperados.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => (
        <Alert 
          key={alert.id}
          variant={alert.type === 'error' ? 'destructive' : 'default'}
          className={
            alert.type === 'warning' 
              ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950'
              : undefined
          }
        >
          {alert.type === 'error' ? (
            <XCircle className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          )}
          <AlertTitle className={
            alert.type === 'warning' 
              ? 'text-yellow-800 dark:text-yellow-200' 
              : undefined
          }>
            {alert.type === 'error' ? 'Erro Crítico' : 'Atenção'}
          </AlertTitle>
          <AlertDescription className={
            alert.type === 'warning' 
              ? 'text-yellow-700 dark:text-yellow-300' 
              : undefined
          }>
            {alert.message}
            {alert.count !== undefined && (
              <span className="ml-2 font-medium">
                ({alert.count} itens afetados)
              </span>
            )}
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
}
